import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

/**
 * Mini sprite-sheet timeline preview.
 * Assumes the sprite sheet image is laid out horizontally with `frames` cells of equal width.
 * Plays back at `fps` frames per second, loop configurable.
 */
export default function SpriteTimeline({ url, frames = 1, fps = 12, loop = true }) {
  const [playing, setPlaying] = useState(false);
  const [frame, setFrame] = useState(0);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const totalFrames = Math.max(1, frames | 0);
  const cellW = imgSize.w ? imgSize.w / totalFrames : 0;

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = url;
    return () => { img.onload = null; };
  }, [url]);

  useEffect(() => {
    if (!playing || totalFrames <= 1) return;
    const step = (t) => {
      if (!lastRef.current) lastRef.current = t;
      const delta = (t - lastRef.current) / 1000;
      if (delta >= 1 / Math.max(1, fps)) {
        setFrame((f) => {
          const nxt = f + 1;
          if (nxt >= totalFrames) return loop ? 0 : totalFrames - 1;
          return nxt;
        });
        lastRef.current = t;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); lastRef.current = 0; };
  }, [playing, fps, loop, totalFrames]);

  if (!url) return null;

  return (
    <div className="mt-2 border border-[#1E1E24] rounded bg-[#0A0A0B] p-2" data-testid="sprite-timeline">
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => setPlaying(p => !p)}
          className="w-6 h-6 rounded bg-[#06B6D4] text-black flex items-center justify-center hover:bg-[#06B6D4]/90"
          data-testid="sprite-timeline-play"
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
        </button>
        <button
          onClick={() => { setFrame(0); setPlaying(false); }}
          className="w-6 h-6 rounded bg-[#1E1E24] text-white flex items-center justify-center hover:bg-[#2E2E34]"
          data-testid="sprite-timeline-reset"
          aria-label="Reset"
        >
          <RotateCcw className="w-3 h-3" />
        </button>
        <span className="text-[10px] font-mono text-zinc-400 ml-auto">Frame {frame + 1}/{totalFrames} · {fps}fps</span>
      </div>

      {/* Preview window — shows the current frame slice */}
      <div className="w-full h-16 bg-[#121214] border border-[#1E1E24] rounded overflow-hidden flex items-center justify-center">
        {cellW > 0 ? (
          <div
            className="h-full bg-no-repeat"
            style={{
              width: `${cellW * (64 / imgSize.h)}px`,
              backgroundImage: `url(${url})`,
              backgroundPosition: `-${frame * cellW * (64 / imgSize.h)}px 0`,
              backgroundSize: `${imgSize.w * (64 / imgSize.h)}px 64px`,
              imageRendering: 'pixelated',
            }}
          />
        ) : (
          <img src={url} alt="" className="h-full object-contain" />
        )}
      </div>

      {/* Scrubber */}
      <input
        type="range" min="0" max={totalFrames - 1} step="1"
        value={frame}
        onChange={(e) => { setFrame(parseInt(e.target.value) || 0); setPlaying(false); }}
        className="w-full mt-2 accent-[#06B6D4]"
        data-testid="sprite-timeline-scrub"
      />
    </div>
  );
}
